<template>
    <v-dialog
      v-model="dialog"
      max-width="500px"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          v-bind="attrs"
          v-on="on"
          class="mx-4"
          x-small
          color="warning"
          fab
          dark
        >
          <v-icon small>mdi-help</v-icon>
        </v-btn>
      </template>

      <v-card>
        <v-card-title
          class="headline grey lighten-2"
          primary-title
        >
          Need help?
          <v-spacer/>
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-expansion-panels focusable accordion class="mt-5">
            <v-expansion-panel>
              <v-expansion-panel-header>Create/Edit a supplier</v-expansion-panel-header>
              <v-expansion-panel-content>
                <p>
                  Create your supplier by clicking on the top right button, or edit by clicking on the pencil at the end of each supplier’s line. Fill the fields with the Name, Country, Currency, Exchange Rate (see Exchange rate concept), and select the connector you want to use.
                </p>
                <p>
                  Name, Country and Currency have no effect on the data, these fields are only labels to name, sort, and filter tables.
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>Exchange rate concept</v-expansion-panel-header>
              <v-expansion-panel-content>
                <p>
                  The exchange rate field allows to compare prices based on your reference currency. The comparaison between suppliers is made by multiplying prices with the exchange rate.
                </p>
                <p>
                  This parameter does not affect data, and all prices remain stored on their original currency.
                </p>
                <p>
                  First define what will be your reference currency, apply an exchange rate of one to all suppliers returning prices in this currency, then for the foreign ones, add the right exchange rate so when you multiply with the price, you align it with you reference currency.
                </p>
                <p>
                  Example: If your reference currency is EUR, suppliers in USD will have an exchange rate of 0.909 if $1 is equivalent to 0.909€.
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>Digi-Key API Connector</v-expansion-panel-header>
              <v-expansion-panel-content>
                <p>
                  Once you have selected this connector, a list of fields will appears to set up the API connector. Here are the list of these fields with their role and possible values.
                </p>
                <ul>
                  <li>
                    CustomerId: Your Customer Id Number (You can find it on Digi-Key’s website, go to « My DigiKey » dashboard, then copy past « Customer Number » on top right.
                  </li>
                  <li>
                    localSite: Two letter code for Digi-Key product website to search on. Different countries sites have different part restrictions, supported languages, and currencies. Acceptable values include: US, CA, JP, UK, DE, AT, BE, DK, FI, GR, IE, IT, LU, NL, NO, PT, ES, KR, HK, SG, CN, TW, AU, FR, IN, NZ, SE, MX, CH, IL, PL, SK, SI, LV, LT, EE, CZ, HU, BG, MY, ZA, RO, TH, PH.
                  </li>
                  <li>
                    localLanguage: Lletter code for language to search on. Language must be supported by the selected site. If searching on keyword, this language is used to find matches. Acceptable values include: en, ja, de, fr, ko, zhs, zht, it, es, he, nl, sv, pl, fi, da, no.
                  </li>
                  <li>
                    LocalCurrency: Three letter code for Currency to return part pricing for. Currency must be supported by the selected site. Acceptable values include: USD, CAD, JPY, GBP, EUR, HKD, SGD, TWD, KRW, AUD, NZD, INR, DKK, NOK, SEK, ILS, CNY, PLN, CHF, CZK, HUF, RON, ZAR, MYR, THB, PHP.
                  </li>
                  <li>
                    localShipToCountry: ISO Code for Country to ship to (example: US, CA, JP, UK, DE, AT, BE, DK, FI, GR, IE, IT, LU, NL, NO, PT, ES, KR, HK, SG, CN, TW, AU, FR, IN, NZ, SE, MX, CH, IL, PL, SK, SI, LV, LT, EE, CZ, HU, BG, MY, ZA, RO, TH, PH)
                  </li>
                </ul>
                <p>
                  Once you have created a supplier with the « Digi-Key API » connector, you have to authenticate by clicking on the open lock. Once authenticated and back to the supplier page, click refresh on the alert bar.
                </p>
                <p>
                  The daily limit is set for all call coming from the website. You may reach the daily limit depending how other accounts requested before you through the day.
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>Mouser API Connector</v-expansion-panel-header>
              <v-expansion-panel-content>
                <p>
                  Once you have selected this connector, a list of fields will appears to set up the API connector. Here are the list of these fields with their role and possible values.
                </p>
                <ul>
                  <li>
                    apiKey: The API key tied to your account. To get this key, go to your Mouser account, then on the left menu, click « Manage » next to « API ». Click on "Learn More" under "Search API", click "Sign up for Search API", fill the form and submit to receive Mouser’s API key.
                  </li>
                  <li>
                    separator: API responses contain prices in localized text format, based on your account language. Enter a comma or a dot to match your account language (example: US = « . », France = « , »).
                  </li>
                </ul>
                <p>
                  Daily limit is 1 000 call/day/apiKey. This means each key you generate can perform 1 000 requests per day.
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>Farnell API Connector</v-expansion-panel-header>
              <v-expansion-panel-content>
                <p>
                  Once you have selected this connector, a list of fields will appears to set up the API connector. Here are the list of these fields with their role and possible values.
                </p>
                <ul>
                  <li>
                    callInfo.apiKey: This is the key linked to your Farnell account. You get get your key on this page: <a href="https://partner.element14.com/docs" target="_blank">https://partner.element14.com/docs</a>. Once you are registered/logged in, go to « My Account », create an application, then a Key will be generated.
                  </li>
                </ul>
                <ul>
                  <li>
                    storeInfo.id: Represents the region of the data store by country. Here is the full list: <a href="https://partner.element14.com/docs/Product_Search_API_REST__Description#storeinfo" target="_blank">https://partner.element14.com/docs/Product_Search_API_REST__Description#storeinfo</a>.
                  </li>
                </ul>
                <ul>
                  <li>
                    SecretKey and customerId are used to generate credentials and get your customer specific pricing. This routine is not yet developed. You can leave both fields blank.
                  </li>
                </ul>
                <p>
                  Daily limit is 1 000 call/day/apiKey. This means each key you generate can perform 1 000 requests per day.
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </v-dialog>
</template>

<script>
export default {
  data: () => ({
    dialog: false
  })
}
</script>
